import { useLazyQuery } from "@apollo/client";
import { compact, defer, first, last, uniqBy } from "lodash";
import moment from "moment";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  GetMeetingOverviewMeetingsQueryQuery,
  GetMeetingOverviewMeetingsQueryQueryVariables,
  MeetingDialogFragmentFragment,
  MeetingsOverviewMeetingFragment,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import getMeetingOverviewMeetingsQuery from "../graphql/get-meeting-overview-meetings-query";
import { MeetingType } from "./meetings-overview-constants";
import OverviewMeetingList from "./overview-meeting-list";

const minimum = 5;

const MeetingOverview = ({
  selectedDay,
  meetingListClassName,
  onClickMeetingLink,
  forceTodayScroll,
  compact: isCompact = false,
  participantFilter = null,
  meetingTypeFilter = MeetingType.ALL,
}: {
  forceTodayScroll: number;
  selectedDay: string;
  meetingListClassName?: string;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
  participantFilter?: number | null;
  meetingTypeFilter?: MeetingType;
}) => {
  const selectedUser = currentUserVar();
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fetchGenRef = useRef(0);
  const [hasMoreInFuture, setHasMoreInFuture] = useState(false);
  const [hasMoreInPast, setHasMoreInPast] = useState(false);
  const [loadMore, setLoadMore] = useState<"past" | "future" | null>(null);

  const [fillerHeight, setFillerHeight] = useState(0);
  const [meetings, setMeetings] = useState<
    (MeetingsOverviewMeetingFragment | MeetingDialogFragmentFragment)[]
  >([]);
  const initialMeetingDisplayedRange = isCompact
    ? {
        start: moment(selectedDay).startOf("day").format(),
        end: moment(selectedDay).endOf("day").format(),
      }
    : {
        start: moment(selectedDay).startOf("isoWeek").format(),
        end: moment(selectedDay).endOf("isoWeek").format(),
      };
  const [meetingDisplayedRange, setMeetingDisplayedRange] = useState<{
    start: string;
    end: string;
  }>(initialMeetingDisplayedRange);

  const [fetchMeetings, { loading }] = useLazyQuery<
    GetMeetingOverviewMeetingsQueryQuery,
    GetMeetingOverviewMeetingsQueryQueryVariables
  >(getMeetingOverviewMeetingsQuery, {});

  const updateFillerHeight = useCallback(() => {
    const targetEl = scrollTargetRef.current || todayRef.current;
    if (!targetEl || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const fillerEl = container.querySelector("#filler");
    const currentFillerHeight = fillerEl ? fillerEl.clientHeight : 0;
    const contentHeight = container.scrollHeight - currentFillerHeight;
    const needed =
      container.clientHeight - (contentHeight - targetEl.offsetTop);
    setFillerHeight(Math.max(0, needed));
  }, []);

  const scrollToRef = (
    ref: React.RefObject<HTMLDivElement | null>,
    instant = false,
  ) => {
    if (ref.current && scrollContainerRef.current) {
      const scrollTop = ref.current.offsetTop;
      scrollContainerRef.current.scrollTo({
        top: scrollTop - 10,
        behavior: instant ? "instant" : "smooth",
      });
    }
  };

  const fetchMoreMeetings = (
    dateRangeStart: string,
    dateRangeEnd: string,
    direction: "future" | "past",
  ) => {
    const gen = fetchGenRef.current;
    return fetchMeetings({
      variables: {
        participants: participantFilter
          ? [participantFilter]
          : [selectedUser.id],
        isOneonone:
          meetingTypeFilter === MeetingType.ALL
            ? undefined
            : meetingTypeFilter === MeetingType.ONE_ON_ONE,
        minimumDateRange: JSON.stringify({
          minimum,
          dateRange: [dateRangeStart, dateRangeEnd],
          direction,
        }),
        orderBy: "start_datetime",
        first: null,
      },
      onCompleted: (response) => {
        if (fetchGenRef.current !== gen) return;
        const newMeetings = response.meetings
          ? assertEdgesNonNull(response.meetings)
          : [];
        setMeetings((prev) => uniqBy([...prev, ...newMeetings], "id"));
        if (loadMore === "future") {
          setHasMoreInFuture(newMeetings.length >= minimum);
        } else if (loadMore === "past") {
          setHasMoreInPast(newMeetings.length >= minimum);
        }

        const firstMeeting = first(newMeetings);
        const lastMeeting = last(newMeetings);

        const sortedDates = compact([
          meetingDisplayedRange.start,
          meetingDisplayedRange.end,
          firstMeeting?.startDatetime &&
            moment(firstMeeting?.startDatetime).startOf("day").format(),
          lastMeeting?.endDatetime &&
            moment(lastMeeting?.endDatetime).endOf("day").format(),
        ]).sort();
        const firstSortedDate = first(sortedDates);
        const lastSortedDate = last(sortedDates);
        if (firstSortedDate && lastSortedDate) {
          setMeetingDisplayedRange({
            start: firstSortedDate,
            end: lastSortedDate,
          });
        }
      },
    });
  };

  useLayoutEffect(() => {
    if (!isCompact) {
      updateFillerHeight();
    }
  }, [meetings, meetingDisplayedRange, isCompact]);

  useEffect(() => {
    if (forceTodayScroll > 0) {
      scrollToRef(todayRef);
    }
  }, [forceTodayScroll]);

  useEffect(() => {
    if (loadMore === null) {
      return;
    }
    const dateRangeStart =
      loadMore === "past"
        ? moment(meetingDisplayedRange.start)
            .subtract(1, "week")
            .startOf("isoWeek")
            .format()
        : moment(meetingDisplayedRange.end)
            .add(1, "day")
            .startOf("day")
            .format();
    const dateRangeEnd =
      loadMore === "past"
        ? moment(meetingDisplayedRange.start)
            .subtract(1, "day")
            .endOf("day")
            .format()
        : moment(meetingDisplayedRange.end)
            .add(1, "week")
            .endOf("isoWeek")
            .format();
    fetchMoreMeetings(dateRangeStart, dateRangeEnd, loadMore).finally(() => {
      setLoadMore(null);
    });
    // only want to fetch more when index changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMore]);

  const initialFetch = () => {
    const gen = fetchGenRef.current;
    fetchMeetings({
      variables: {
        participants: participantFilter
          ? [participantFilter]
          : [selectedUser.id],
        isOneonone:
          meetingTypeFilter === MeetingType.ALL
            ? undefined
            : meetingTypeFilter === MeetingType.ONE_ON_ONE,
        orderBy: "start_datetime",
        first: null,
        minimumDateRange: JSON.stringify({
          minimum: isCompact ? 0 : minimum,
          dateRange: [
            initialMeetingDisplayedRange.start,
            initialMeetingDisplayedRange.end,
          ],
          direction: "future",
        }),
      },
      onCompleted: (response) => {
        if (fetchGenRef.current !== gen) return;
        const newMeetings = response.meetings
          ? assertEdgesNonNull(response.meetings)
          : [];
        setMeetings((prev) => uniqBy([...prev, ...newMeetings], "id"));
        setHasMoreInPast(true);
        setHasMoreInFuture(newMeetings.length >= minimum);
        if (!isCompact) {
          const firstMeeting = first(newMeetings);
          const lastMeeting = last(newMeetings);

          const sortedDates = compact([
            meetingDisplayedRange.start,
            meetingDisplayedRange.end,
            firstMeeting?.startDatetime &&
              moment(firstMeeting?.startDatetime).startOf("day").format(),
            lastMeeting?.endDatetime &&
              moment(lastMeeting?.endDatetime).endOf("day").format(),
          ]).sort();
          const firstSortedDate = first(sortedDates);
          const lastSortedDate = last(sortedDates);
          if (firstSortedDate && lastSortedDate) {
            setMeetingDisplayedRange({
              start: firstSortedDate,
              end: lastSortedDate,
            });

            // If there aren't enough meetings after the target day,
            // fetch more in the future so the view isn't empty below
            const targetDay = selectedDay || moment().format("YYYY-MM-DD");
            const meetingsAfterTarget = newMeetings.filter(
              (m) =>
                m.startDatetime &&
                moment(m.startDatetime).isAfter(moment(targetDay).endOf("day")),
            );
            if (meetingsAfterTarget.length < 4) {
              const backfillMin = 4 - meetingsAfterTarget.length;
              const backfillStart = moment(lastSortedDate)
                .add(1, "day")
                .startOf("day")
                .format();
              const backfillEnd = moment(lastSortedDate)
                .add(1, "week")
                .endOf("isoWeek")
                .format();
              fetchMeetings({
                variables: {
                  participants: participantFilter
                    ? [participantFilter]
                    : [selectedUser.id],
                  isOneonone:
                    meetingTypeFilter === MeetingType.ALL
                      ? undefined
                      : meetingTypeFilter === MeetingType.ONE_ON_ONE,
                  minimumDateRange: JSON.stringify({
                    minimum: backfillMin,
                    dateRange: [backfillStart, backfillEnd],
                    direction: "future",
                  }),
                  orderBy: "start_datetime",
                  first: null,
                },
                onCompleted: (backfillResponse) => {
                  if (fetchGenRef.current !== gen) return;
                  const backfillMeetings = backfillResponse.meetings
                    ? assertEdgesNonNull(backfillResponse.meetings)
                    : [];
                  setMeetings((prev) =>
                    uniqBy([...prev, ...backfillMeetings], "id"),
                  );
                  setHasMoreInFuture(backfillMeetings.length >= backfillMin);
                  const backfillLast = last(backfillMeetings);
                  if (backfillLast?.endDatetime) {
                    setMeetingDisplayedRange((prev) => ({
                      start: prev.start,
                      end: moment(backfillLast.endDatetime)
                        .endOf("day")
                        .format(),
                    }));
                  }
                },
              });
            }
          }
        }
        if (!isCompact) {
          defer(() => {
            scrollToRef(scrollTargetRef, true);
          });
        }
      },
    });
  };

  useEffect(() => {
    // Increment generation to invalidate any in-flight fetches
    fetchGenRef.current += 1;
    // reset meetings and pagination states
    setMeetings([]);
    setHasMoreInFuture(false);
    setHasMoreInPast(false);
    setMeetingDisplayedRange(initialMeetingDisplayedRange);
    // defer fetch so the state is updated, when function is called.
    defer(() => {
      initialFetch();
    });
  }, [participantFilter, meetingTypeFilter]);

  // When toggling compact mode or changing selected day, re-fetch without
  // clearing existing meetings to avoid a loading flash
  useEffect(() => {
    fetchGenRef.current += 1;
    setMeetingDisplayedRange(initialMeetingDisplayedRange);
    if (isCompact) {
      setFillerHeight(0);
    }
    defer(() => {
      initialFetch();
    });
  }, [isCompact, selectedDay]);

  useEffect(() => {
    initialFetch();
  }, []);

  const meetingsToDisplay = isCompact
    ? meetings.filter((m) =>
        moment(m.startDatetime).isSame(moment(selectedDay), "day"),
      )
    : meetings;

  return (
    <div
      className={classNames(
        "@container/overview-meetings",
        isCompact ? "relative" : "h-full",
      )}
    >
      <div
        className={classNames(
          isCompact
            ? "px-3 pt-1 pb-4 overflow-y-auto scrollbar-hidden"
            : "absolute inset-0 overflow-y-scroll scrollbar-hidden pb-12 px-3 pt-4",
          meetingListClassName,
        )}
        ref={scrollContainerRef}
      >
        {/* Main loader on first page hit */}
        {loading && meetingsToDisplay.length === 0 ? (
          <Loading className="p-6" size={6}>
            Loading meetings...
          </Loading>
        ) : meetingsToDisplay.length === 0 ? (
          <div className="text-gray-500 px-2">No meetings.</div>
        ) : (
          <OverviewMeetingList
            ref={scrollTargetRef}
            meetings={meetingsToDisplay}
            loading={loading}
            participantFilter={participantFilter}
            meetingTypeFilter={meetingTypeFilter}
            meetingDisplayedRange={
              isCompact
                ? {
                    start: moment(selectedDay).startOf("day").format(),
                    end: moment(selectedDay).endOf("day").format(),
                  }
                : meetingDisplayedRange
            }
            onClickMeetingLink={onClickMeetingLink}
            canLoadMoreInFuture={hasMoreInFuture}
            canLoadMoreInPast={hasMoreInPast}
            loadMore={loadMore}
            onChangeLoadMore={setLoadMore}
            isShowingWeekends={true}
            fillerHeight={fillerHeight}
            compact={isCompact}
            scrollToDay={selectedDay}
            todayRef={todayRef}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingOverview;
